import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { Plus, Clock, CheckCircle, Hourglass, CreditCard } from "@phosphor-icons/react";

const STATUS_META = {
    pending_review: { label: "Pending review", color: "bg-amber-500/20 text-amber-300 border-amber-500/30", I: Hourglass },
    quoted: { label: "Quoted", color: "bg-blue-500/20 text-blue-300 border-blue-500/30", I: Clock },
    approved: { label: "Approved", color: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30", I: CheckCircle },
    in_progress: { label: "In progress", color: "bg-purple-500/20 text-purple-300 border-purple-500/30", I: Hourglass },
    completed: { label: "Completed", color: "bg-cyan-500/20 text-cyan-300 border-cyan-500/30", I: CheckCircle },
    paid: { label: "Paid", color: "bg-gold/20 text-gold border-gold/30", I: CreditCard },
    cancelled: { label: "Cancelled", color: "bg-red-500/20 text-red-300 border-red-500/30", I: Clock },
};

function Badge({ status }) {
    const m = STATUS_META[status] || STATUS_META.pending_review;
    const I = m.I;
    return (
        <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-sm border ${m.color}`}>
            <I size={12} weight="duotone" /> {m.label}
        </span>
    );
}

export default function DashboardPage() {
    const [tasks, setTasks] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const { data } = await http.get("/tasks");
                setTasks(data);
            } catch (e) { setError(formatApiError(e)); }
            finally { setLoading(false); }
        })();
    }, []);

    return (
        <div data-testid="dashboard-page" className="max-w-7xl mx-auto px-6 sm:px-10 py-12">
            <div className="flex items-end justify-between mb-10">
                <div>
                    <div className="label-eyebrow mb-3">/ My account</div>
                    <h1 className="font-serif text-4xl sm:text-5xl">Your <span className="italic gold-text">tasks</span></h1>
                </div>
                <Link to="/tasks/new" data-testid="dashboard-new-task" className="btn-gold">
                    <Plus size={16} weight="bold" className="inline mr-2" /> New task
                </Link>
            </div>

            {error && <div className="text-red-400 text-sm mb-6">{error}</div>}

            {loading ? (
                <div className="text-slate-500">Loading…</div>
            ) : tasks.length === 0 ? (
                <div className="bg-slate-900/50 border border-white/5 rounded-sm p-16 text-center">
                    <div className="font-serif text-3xl mb-3">No tasks yet</div>
                    <p className="text-slate-400 mb-8">Submit your first task and we'll quote it in under 2 minutes.</p>
                    <Link to="/tasks/new" className="btn-gold inline-block">Submit a task</Link>
                </div>
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="tasks-list">
                    {tasks.map((t) => (
                        <Link
                            key={t.id}
                            to={`/tasks/${t.id}`}
                            data-testid={`task-card-${t.id}`}
                            className="bg-slate-900/60 border border-white/5 p-6 rounded-sm hover:-translate-y-1 hover:border-gold/30 transition"
                        >
                            <div className="flex justify-between items-start gap-3">
                                <div className="font-serif text-xl leading-snug">{t.title}</div>
                                <Badge status={t.status} />
                            </div>
                            <div className="text-xs uppercase tracking-widest text-slate-500 mt-2">{t.category}</div>
                            <p className="text-sm text-slate-400 mt-4 line-clamp-3">{t.description}</p>
                            <div className="mt-6 pt-4 border-t border-white/5 flex justify-between items-end">
                                <div className="text-xs text-slate-500">
                                    {t.butler_quote_amount ? "Butler quote" : "AI quote"}
                                </div>
                                <div className="font-serif text-2xl gold-text">
                                    ${(t.final_amount ?? t.butler_quote_amount ?? t.ai_quote_amount)?.toFixed(2)}
                                </div>
                            </div>
                        </Link>
                    ))}
                </div>
            )}
        </div>
    );
}
